import type {
  CableRoute,
  DataPage,
  ImageData,
  LEDPanel,
  MireConnection,
  MirePowerConnection,
  PanelConnection,
  PowerConnection,
  PowerPage,
  WorkspaceElement
} from '@/types/synoptique'

const CELL_MARKER = '_cell_'

export interface CablePoint {
  x: number
  y: number
}

/** Patchs applicables atomiquement sur une page (Data ou Power) */
export interface DataPageWiringPatch {
  cableRoutes?: CableRoute[]
  panelConnections?: PanelConnection[]
  mireConnections?: MireConnection[]
}

export interface PowerPageWiringPatch {
  cableRoutes?: CableRoute[]
  powerConnections?: PowerConnection[]
  mirePowerConnections?: MirePowerConnection[]
}

// =============================================================================
// Helpers bas niveau
// =============================================================================

function elementCenter(el: WorkspaceElement): CablePoint {
  return {
    x: el.position.x + el.size.width / 2,
    y: el.position.y + el.size.height / 2
  }
}

/** Centre d'une cellule mire (imageId + row/col 1-based), null si config invalide */
export function getMireCellCenter(
  imageElement: WorkspaceElement,
  row: number,
  col: number
): CablePoint | null {
  const ledWall = imageElement.data?.mireConfig?.ledWallMode
  const columns = ledWall?.columns
  const rows = ledWall?.rows
  if (!columns || !rows || columns <= 0 || rows <= 0) return null
  if (row < 1 || row > rows || col < 1 || col > columns) return null
  const cellWidth = imageElement.size.width / columns
  const cellHeight = imageElement.size.height / rows
  return {
    x: imageElement.position.x + (col - 1) * cellWidth + cellWidth / 2,
    y: imageElement.position.y + (row - 1) * cellHeight + cellHeight / 2
  }
}

export function mireCellEndpointId(imageId: string, row: number, col: number): string {
  return `${imageId}${CELL_MARKER}${row}_${col}`
}

/** Découpe un endpoint « imageId_cell_row_col » ; null si ce n'est pas une cellule valide */
export function parseCellEndpoint(id: string): { imageId: string; row: number; col: number } | null {
  const idx = id.lastIndexOf(CELL_MARKER)
  if (idx <= 0) return null
  const imageId = id.slice(0, idx)
  const parts = id.slice(idx + CELL_MARKER.length).split('_')
  if (parts.length !== 2) return null
  const row = Number(parts[0])
  const col = Number(parts[1])
  if (!Number.isInteger(row) || !Number.isInteger(col) || row < 1 || col < 1) return null
  return { imageId, row, col }
}

/** Vrai si une route possède au moins 2 points finis (rendu sûr) */
export function hasValidCablePoints(route: { points?: Array<{ x: number; y: number }> }): boolean {
  const points = route.points
  return Array.isArray(points) && points.length >= 2 &&
    points.every(p =>
      !!p && typeof p.x === 'number' && Number.isFinite(p.x) &&
      typeof p.y === 'number' && Number.isFinite(p.y)
    )
}

/** Points d'ancrage directionnels (0.25/0.75) entre deux éléments — sémantique du clic panneau */
function directionalPoints(fromEl: WorkspaceElement, toEl: WorkspaceElement): { fromPoint: CablePoint; toPoint: CablePoint } {
  const dx = toEl.position.x - fromEl.position.x
  const dy = toEl.position.y - fromEl.position.y
  const isHorizontal = Math.abs(dx) > Math.abs(dy)

  if (isHorizontal) {
    return {
      fromPoint: {
        x: dx > 0 ? fromEl.position.x + fromEl.size.width * 0.75 : fromEl.position.x + fromEl.size.width * 0.25,
        y: fromEl.position.y + fromEl.size.height / 2
      },
      toPoint: {
        x: dx > 0 ? toEl.position.x + toEl.size.width * 0.25 : toEl.position.x + toEl.size.width * 0.75,
        y: toEl.position.y + toEl.size.height / 2
      }
    }
  }
  return {
    fromPoint: {
      x: fromEl.position.x + fromEl.size.width / 2,
      y: dy > 0 ? fromEl.position.y + fromEl.size.height * 0.75 : fromEl.position.y + fromEl.size.height * 0.25
    },
    toPoint: {
      x: toEl.position.x + toEl.size.width / 2,
      y: dy > 0 ? toEl.position.y + toEl.size.height * 0.75 : toEl.position.y + toEl.size.height * 0.25
    }
  }
}

function newCableId(prefix: 'data' | 'power'): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

// =============================================================================
// Mutations atomiques — MIRE (Data)
// =============================================================================

/**
 * Ajoute une connexion de cellule mire + le câble associé dans la même opération.
 * Refuse si la cellule est déjà connectée, si l'image/processeur est absent,
 * ou si la cellule cible est hors grille. Calcule order = max(ordre chaîne) + 1
 * et chaîne depuis la connexion d'ordre maximal.
 */
export function appendMireDataConnection(
  page: Pick<DataPage, 'workspaceElements' | 'cableRoutes' | 'mireConnections'>,
  args: { imageId: string; row: number; col: number; processorId: string; port: number; color: string }
): DataPageWiringPatch | null {
  const { imageId, row, col, processorId, port, color } = args
  const imageElement = page.workspaceElements.find(el => el.id === imageId && el.type === 'image')
  if (!imageElement) return null

  const connections = page.mireConnections || []
  if (connections.some(c => c.imageId === imageId && c.gridPosition.row === row && c.gridPosition.col === col)) {
    return null // cellule déjà connectée — jamais de doublon
  }

  const cellCenter = getMireCellCenter(imageElement, row, col)
  if (!cellCenter) return null

  const chain = connections.filter(c =>
    c.imageId === imageId && c.processorId === processorId && c.port === port
  )
  const maxOrder = chain.reduce((m, c) => Math.max(m, c.order || 0), 0)
  const lastConn = chain.find(c => (c.order || 0) === maxOrder)

  let fromPoint: CablePoint
  let fromId: string
  if (!lastConn) {
    const processor = page.workspaceElements.find(el => el.id === processorId)
    if (!processor) return null
    fromPoint = elementCenter(processor)
    fromId = processorId
  } else {
    const lastCenter = getMireCellCenter(imageElement, lastConn.gridPosition.row, lastConn.gridPosition.col)
    if (!lastCenter) return null
    fromPoint = lastCenter
    fromId = mireCellEndpointId(imageId, lastConn.gridPosition.row, lastConn.gridPosition.col)
  }

  const newCable: CableRoute = {
    id: newCableId('data'),
    type: 'data',
    from: fromId,
    to: mireCellEndpointId(imageId, row, col),
    length: 0,
    color,
    points: [fromPoint, cellCenter]
  }
  const newConnection: MireConnection = {
    imageId,
    processorId,
    port,
    gridPosition: { row, col },
    order: maxOrder + 1
  }

  return {
    cableRoutes: [...(page.cableRoutes || []), newCable],
    mireConnections: [...connections, newConnection]
  }
}

/**
 * Équivalent Power : connexion cellule mire + câble power dans la même opération.
 */
export function appendMirePowerConnection(
  page: Pick<PowerPage, 'workspaceElements' | 'cableRoutes' | 'mirePowerConnections'>,
  args: { imageId: string; row: number; col: number; cabinetId: string; outlet: number; color: string }
): PowerPageWiringPatch | null {
  const { imageId, row, col, cabinetId, outlet, color } = args
  const imageElement = page.workspaceElements.find(el => el.id === imageId && el.type === 'image')
  if (!imageElement) return null

  const connections = page.mirePowerConnections || []
  if (connections.some(c => c.imageId === imageId && c.gridPosition.row === row && c.gridPosition.col === col)) {
    return null
  }

  const cellCenter = getMireCellCenter(imageElement, row, col)
  if (!cellCenter) return null

  const chain = connections.filter(c =>
    c.imageId === imageId && c.cabinetId === cabinetId && c.outlet === outlet
  )
  const maxOrder = chain.reduce((m, c) => Math.max(m, c.order || 0), 0)
  const lastConn = chain.find(c => (c.order || 0) === maxOrder)

  let fromPoint: CablePoint
  let fromId: string
  if (!lastConn) {
    const cabinet = page.workspaceElements.find(el => el.id === cabinetId)
    if (!cabinet) return null
    fromPoint = elementCenter(cabinet)
    fromId = cabinetId
  } else {
    const lastCenter = getMireCellCenter(imageElement, lastConn.gridPosition.row, lastConn.gridPosition.col)
    if (!lastCenter) return null
    fromPoint = lastCenter
    fromId = mireCellEndpointId(imageId, lastConn.gridPosition.row, lastConn.gridPosition.col)
  }

  const newCable: CableRoute = {
    id: newCableId('power'),
    type: 'power',
    from: fromId,
    to: mireCellEndpointId(imageId, row, col),
    length: 0,
    color,
    points: [fromPoint, cellCenter]
  }
  const newConnection: MirePowerConnection = {
    imageId,
    cabinetId,
    outlet,
    gridPosition: { row, col },
    order: maxOrder + 1
  }

  return {
    cableRoutes: [...(page.cableRoutes || []), newCable],
    mirePowerConnections: [...connections, newConnection]
  }
}

// =============================================================================
// Mutations atomiques — PANNEAUX (Data / Power)
// =============================================================================

/**
 * Connexion panneau data atomique : câble + PanelConnection dans le même patch.
 * - Refuse si le panneau est déjà connecté sur ce port/processeur.
 * - order = max(ordre chaîne) + 1 ; chaîne depuis la connexion d'ordre max.
 */
export function appendPanelDataConnection(
  page: Pick<DataPage, 'workspaceElements' | 'cableRoutes' | 'panelConnections'>,
  args: { targetPanelId: string; processorId: string; port: number; color: string }
): DataPageWiringPatch | null {
  const { targetPanelId, processorId, port, color } = args
  const connections = page.panelConnections || []

  if (connections.some(c => c.panelId === targetPanelId && c.processorId === processorId && c.port === port)) {
    return null // déjà connecté sur ce port
  }

  const target = page.workspaceElements.find(el => el.id === targetPanelId)
  if (!target) return null

  const chain = connections.filter(c => c.processorId === processorId && c.port === port)
  const maxOrder = chain.reduce((m, c) => Math.max(m, c.order || 0), 0)

  let fromPoint: CablePoint
  let toPoint: CablePoint
  let fromId: string

  if (chain.length === 0) {
    const processor = page.workspaceElements.find(el => el.id === processorId)
    if (!processor) return null
    const pts = directionalPoints(processor, target)
    fromPoint = pts.fromPoint
    toPoint = pts.toPoint
    fromId = processorId
  } else {
    const lastConn = chain.find(c => (c.order || 0) === maxOrder)!
    const fromPanel = page.workspaceElements.find(el => el.id === lastConn.panelId)
    if (!fromPanel) return null
    fromId = lastConn.panelId
    fromPoint = elementCenter(fromPanel)
    toPoint = elementCenter(target)
  }

  const newCable: CableRoute = {
    id: newCableId('data'),
    type: 'data',
    from: fromId,
    to: targetPanelId,
    length: 0,
    color,
    points: [fromPoint, toPoint]
  }
  const newConnection: PanelConnection = {
    panelId: targetPanelId,
    processorId,
    port,
    order: maxOrder + 1
  }

  return {
    cableRoutes: [...(page.cableRoutes || []), newCable],
    panelConnections: [...connections, newConnection]
  }
}

/**
 * Équivalent Power : câble + PowerConnection dans le même patch.
 * - Refuse si le panneau est déjà alimenté (toute prise — invariant d'origine).
 * - order = max(ordre chaîne) + 1 ; chaîne depuis la connexion d'ordre max.
 */
export function appendPanelPowerConnection(
  page: Pick<PowerPage, 'workspaceElements' | 'cableRoutes' | 'powerConnections'>,
  args: { targetPanelId: string; cabinetId: string; outlet: number; color: string }
): PowerPageWiringPatch | null {
  const { targetPanelId, cabinetId, outlet, color } = args
  const connections = page.powerConnections || []

  if (connections.some(c => c.panelId === targetPanelId)) {
    return null // déjà alimenté
  }

  const target = page.workspaceElements.find(el => el.id === targetPanelId)
  if (!target) return null

  const chain = connections.filter(c => c.cabinetId === cabinetId && c.outlet === outlet)
  const maxOrder = chain.reduce((m, c) => Math.max(m, c.order || 0), 0)

  let fromPoint: CablePoint
  let toPoint: CablePoint
  let fromId: string

  if (chain.length === 0) {
    const cabinet = page.workspaceElements.find(el => el.id === cabinetId)
    if (!cabinet) return null
    const pts = directionalPoints(cabinet, target)
    fromPoint = pts.fromPoint
    toPoint = pts.toPoint
    fromId = cabinetId
  } else {
    const lastConn = chain.find(c => (c.order || 0) === maxOrder)!
    const fromPanel = page.workspaceElements.find(el => el.id === lastConn.panelId)
    if (!fromPanel) return null
    fromId = lastConn.panelId
    fromPoint = elementCenter(fromPanel)
    toPoint = elementCenter(target)
  }

  const newCable: CableRoute = {
    id: newCableId('power'),
    type: 'power',
    from: fromId,
    to: targetPanelId,
    length: 0,
    color,
    points: [fromPoint, toPoint]
  }
  const newConnection: PowerConnection = {
    panelId: targetPanelId,
    cabinetId,
    outlet,
    order: maxOrder + 1
  }

  return {
    cableRoutes: [...(page.cableRoutes || []), newCable],
    powerConnections: [...connections, newConnection]
  }
}

// =============================================================================
// Sanitisation au chargement
// =============================================================================

function safeOrder(order: unknown): number {
  return typeof order === 'number' && Number.isFinite(order) ? order : 0
}

/**
 * Renumérote les ordres d'un groupe de connexions en suivant la chaîne de câbles
 * réelle (from → to). Les connexions inatteignables conservent leur ordre
 * relatif à la fin. Résultat : numérotation toujours cohérente avec les flèches.
 */
function renumberByCableChain<Conn extends { order: number }>(
  conns: Conn[],
  cables: CableRoute[],
  endpointId: (c: Conn) => string,
  sourceId: (c: Conn) => string,
  groupKey: (c: Conn) => string
): Conn[] {
  const groups = new Map<string, Conn[]>()
  for (const conn of conns) {
    const key = groupKey(conn)
    const group = groups.get(key) || []
    group.push(conn)
    groups.set(key, group)
  }

  const out: Conn[] = []
  for (const group of groups.values()) {
    const byEndpoint = new Map(group.map(c => [endpointId(c), c]))
    const incomingSource = new Map<string, string>()
    const nextFrom = new Map<string, string>()
    for (const cable of cables) {
      if (byEndpoint.has(cable.to)) {
        if (!incomingSource.has(cable.to)) incomingSource.set(cable.to, cable.from)
        if (!nextFrom.has(cable.from)) nextFrom.set(cable.from, cable.to)
      }
    }

    // Départs : connexion dont le câble entrant vient de la source (processeur/armoire)
    // puis connexions sans câble entrant (orphelines de chaîne)
    const sortedGroup = [...group].sort((a, b) => safeOrder(a.order) - safeOrder(b.order))
    const starts = [
      ...sortedGroup.filter(c => incomingSource.get(endpointId(c)) === sourceId(c)),
      ...sortedGroup.filter(c => !incomingSource.has(endpointId(c)))
    ]

    const visited = new Set<Conn>()
    const walk = (start: Conn) => {
      let current: Conn | undefined = start
      while (current && !visited.has(current)) {
        visited.add(current)
        out.push(current)
        const nextId = nextFrom.get(endpointId(current))
        current = nextId ? byEndpoint.get(nextId) : undefined
      }
    }
    starts.forEach(walk)
    // Inatteignables (cycles orphelins, câbles manquants) : à la fin, ordre relatif conservé
    sortedGroup.forEach(c => { if (!visited.has(c)) { visited.add(c); out.push(c) } })
  }

  // Renuméroter 1..n dans chaque groupe, en conservant l'objet d'origine
  const counters = new Map<string, number>()
  return out.map(conn => {
    const key = groupKey(conn)
    const order = (counters.get(key) || 0) + 1
    counters.set(key, order)
    return conn.order === order ? conn : { ...conn, order }
  })
}

function dedupeBy<T>(items: T[], key: (item: T) => string): T[] {
  const seen = new Set<string>()
  return items.filter(item => {
    const k = key(item)
    if (seen.has(k)) return false
    seen.add(k)
    return true
  })
}

/** Répare ou supprime les câbles d'une page ; type attendu = 'data' | 'power' */
function sanitizeCables(
  cables: CableRoute[],
  expectedType: 'data' | 'power',
  elementsById: Map<string, WorkspaceElement>,
  validCellIds: Set<string>
): CableRoute[] {
  const endpointOk = (id: string): boolean => {
    const cell = parseCellEndpoint(id)
    if (cell) {
      // Cellule mire : image existante + connexion correspondante requise
      return elementsById.has(cell.imageId) && validCellIds.has(id)
    }
    return elementsById.has(id)
  }

  return (cables || [])
    .filter(route => route && route.type === expectedType)
    .filter(route => endpointOk(route.from) && endpointOk(route.to))
    .map(route => {
      if (hasValidCablePoints(route)) return route
      // Réparer les points depuis les positions live (centre d'élément ou de cellule)
      const resolve = (id: string): CablePoint | null => {
        const cell = parseCellEndpoint(id)
        if (cell) {
          const img = elementsById.get(cell.imageId)
          return img ? getMireCellCenter(img, cell.row, cell.col) : null
        }
        const el = elementsById.get(id)
        return el ? elementCenter(el) : null
      }
      const from = resolve(route.from)
      const to = resolve(route.to)
      if (!from || !to) return route // sera filtré juste après
      return { ...route, points: [from, to] }
    })
    .filter(route => hasValidCablePoints(route))
}

/** Assainit une page Data : dédup connexions, supprime orphelins, normalise les ordres */
export function sanitizeDataPage(page: DataPage): DataPage {
  const elements = Array.isArray(page.workspaceElements) ? page.workspaceElements : []
  const elementsById = new Map(elements.map(e => [e.id, e]))

  // Connexions panneaux : existantes uniquement, dédup par (panel, processeur, port)
  const panelConnections = dedupeBy(
    (page.panelConnections || []).filter(c =>
      elementsById.has(c.panelId) && elementsById.has(c.processorId)
    ),
    c => `${c.panelId}|${c.processorId}|${c.port}`
  )

  // Connexions mire : image + processeur existants, dédup par cellule, ordres par (image, proc, port)
  const mireConnections = dedupeBy(
    (page.mireConnections || []).filter(c =>
      elementsById.has(c.imageId) && elementsById.has(c.processorId) &&
      c.gridPosition && Number.isInteger(c.gridPosition.row) && Number.isInteger(c.gridPosition.col)
    ),
    c => `${c.imageId}|${c.gridPosition.row}|${c.gridPosition.col}`
  )

  const validCellIds = new Set(mireConnections.map(c =>
    mireCellEndpointId(c.imageId, c.gridPosition.row, c.gridPosition.col)
  ))
  const cableRoutes = sanitizeCables(page.cableRoutes || [], 'data', elementsById, validCellIds)

  return {
    ...page,
    workspaceElements: elements,
    cableRoutes,
    panelConnections: renumberByCableChain(
      panelConnections,
      cableRoutes,
      c => c.panelId,
      c => c.processorId,
      c => `${c.processorId}|${c.port}`
    ),
    mireConnections: renumberByCableChain(
      mireConnections,
      cableRoutes,
      c => mireCellEndpointId(c.imageId, c.gridPosition.row, c.gridPosition.col),
      c => c.processorId,
      c => `${c.imageId}|${c.processorId}|${c.port}`
    )
  }
}

/** Assainit une page Power : même traitement que Data */
export function sanitizePowerPage(page: PowerPage): PowerPage {
  const elements = Array.isArray(page.workspaceElements) ? page.workspaceElements : []
  const elementsById = new Map(elements.map(e => [e.id, e]))

  const powerConnections = dedupeBy(
    (page.powerConnections || []).filter(c =>
      elementsById.has(c.panelId) && elementsById.has(c.cabinetId)
    ),
    c => `${c.panelId}|${c.cabinetId}|${c.outlet}`
  )

  const mirePowerConnections = dedupeBy(
    (page.mirePowerConnections || []).filter(c =>
      elementsById.has(c.imageId) && elementsById.has(c.cabinetId) &&
      c.gridPosition && Number.isInteger(c.gridPosition.row) && Number.isInteger(c.gridPosition.col)
    ),
    c => `${c.imageId}|${c.gridPosition.row}|${c.gridPosition.col}`
  )

  const validCellIds = new Set(mirePowerConnections.map(c =>
    mireCellEndpointId(c.imageId, c.gridPosition.row, c.gridPosition.col)
  ))
  const cableRoutes = sanitizeCables(page.cableRoutes || [], 'power', elementsById, validCellIds)

  return {
    ...page,
    workspaceElements: elements,
    cableRoutes,
    powerConnections: renumberByCableChain(
      powerConnections,
      cableRoutes,
      c => c.panelId,
      c => c.cabinetId,
      c => `${c.cabinetId}|${c.outlet}`
    ),
    mirePowerConnections: renumberByCableChain(
      mirePowerConnections,
      cableRoutes,
      c => mireCellEndpointId(c.imageId, c.gridPosition.row, c.gridPosition.col),
      c => c.cabinetId,
      c => `${c.imageId}|${c.cabinetId}|${c.outlet}`
    )
  }
}

// =============================================================================
// Liaisons armoire → armoire (alimentation générale, hors prise)
// =============================================================================

/** Forme minimale d'un câble pour la détection de liaison armoire */
export interface CabinetCableShape {
  id?: string
  type: string
  from: string
  to: string
}

/** Vrai si le câble relie deux armoires (liaison générale, pas une prise→panneau) */
export function isCabinetLinkCable(cable: CabinetCableShape, elementsById: Map<string, WorkspaceElement>): boolean {
  if (cable.type !== 'power') return false
  if (cable.from.includes(CELL_MARKER) || cable.to.includes(CELL_MARKER)) return false
  return elementsById.get(cable.from)?.type === 'cabinet' &&
    elementsById.get(cable.to)?.type === 'cabinet'
}

export type CabinetLinkRejection = 'same' | 'not-cabinets' | 'duplicate' | 'already-fed' | 'cycle'

/**
 * Valide une liaison armoire source → cible sur un état donné.
 * Retourne null si OK, sinon la raison du refus.
 */
export function validateCabinetPowerLink(
  elements: WorkspaceElement[],
  cableRoutes: CabinetCableShape[],
  sourceCabinetId: string,
  targetCabinetId: string
): CabinetLinkRejection | null {
  if (sourceCabinetId === targetCabinetId) return 'same'

  const elementsById = new Map(elements.map(e => [e.id, e]))
  const source = elementsById.get(sourceCabinetId)
  const target = elementsById.get(targetCabinetId)
  if (!source || !target || source.type !== 'cabinet' || target.type !== 'cabinet') return 'not-cabinets'

  const links = (cableRoutes || []).filter(c => isCabinetLinkCable(c, elementsById))

  // Doublon : même liaison déjà présente (dans un sens ou l'autre)
  if (links.some(l =>
    (l.from === sourceCabinetId && l.to === targetCabinetId) ||
    (l.from === targetCabinetId && l.to === sourceCabinetId)
  )) return 'duplicate'

  // Une seule source d'alimentation par armoire
  if (links.some(l => l.to === targetCabinetId)) return 'already-fed'

  // Anti-cycle : la cible ne doit pas déjà alimenter la source en amont
  const feeds = new Map<string, string[]>()
  for (const l of links) {
    const list = feeds.get(l.from) || []
    list.push(l.to)
    feeds.set(l.from, list)
  }
  const seen = new Set<string>()
  const stack = [targetCabinetId]
  while (stack.length) {
    const cur = stack.pop()!
    if (cur === sourceCabinetId) return 'cycle'
    if (seen.has(cur)) continue
    seen.add(cur)
    for (const next of feeds.get(cur) || []) stack.push(next)
  }

  return null
}

/**
 * Ajoute une liaison générale armoire source → armoire cible (câble power direct).
 * La validation est déléguée à validateCabinetPowerLink.
 */
export function appendCabinetPowerLink(
  page: Pick<PowerPage, 'workspaceElements' | 'cableRoutes'>,
  args: { sourceCabinetId: string; targetCabinetId: string; color: string }
): PowerPageWiringPatch | null {
  const { sourceCabinetId, targetCabinetId, color } = args

  if (validateCabinetPowerLink(page.workspaceElements, page.cableRoutes || [], sourceCabinetId, targetCabinetId)) {
    return null
  }

  const elementsById = new Map(page.workspaceElements.map(e => [e.id, e]))
  const source = elementsById.get(sourceCabinetId)!
  const target = elementsById.get(targetCabinetId)!
  const pts = directionalPoints(source, target)
  const newCable: CableRoute = {
    id: newCableId('power'),
    type: 'power',
    from: sourceCabinetId,
    to: targetCabinetId,
    length: 0,
    color,
    points: [pts.fromPoint, pts.toPoint]
  }

  return { cableRoutes: [...(page.cableRoutes || []), newCable] }
}

/**
 * Charge totale (W) par armoire, liaisons incluses :
 * conso des panneaux + cellules mire alimentés par ses prises,
 * + charge totale des armoires qu'elle alimente (récursif, anti-cycle).
 */
export function computeCabinetLoadMap(
  elements: WorkspaceElement[],
  powerConnections: Pick<PowerConnection, 'panelId' | 'cabinetId'>[],
  mirePowerConnections: Pick<MirePowerConnection, 'imageId' | 'cabinetId'>[],
  cableRoutes: CabinetCableShape[]
): { direct: Map<string, number>; total: Map<string, number> } {
  const elementsById = new Map(elements.map(e => [e.id, e]))

  // Charge propre : panneaux + cellules mire alimentés directement par l'armoire
  const directLoad = new Map<string, number>()
  const addLoad = (cabinetId: string, watts: number) => {
    directLoad.set(cabinetId, (directLoad.get(cabinetId) || 0) + watts)
  }
  for (const conn of powerConnections) {
    const panel = elementsById.get(conn.panelId)
    if (panel?.type === 'panel') {
      const w = (panel.data as LEDPanel).powerConsumption
      if (typeof w === 'number' && Number.isFinite(w)) addLoad(conn.cabinetId, w)
    }
  }
  for (const conn of mirePowerConnections) {
    const img = elementsById.get(conn.imageId)
    const w = img?.type === 'image' ? (img.data as ImageData).linkedPanel?.powerConsumption : undefined
    if (typeof w === 'number' && Number.isFinite(w)) addLoad(conn.cabinetId, w)
  }

  // Graphe des liaisons armoire → armoire
  const children = new Map<string, string[]>()
  for (const cable of cableRoutes) {
    if (!isCabinetLinkCable(cable, elementsById)) continue
    const list = children.get(cable.from) || []
    list.push(cable.to)
    children.set(cable.from, list)
  }

  const totals = new Map<string, number>()
  const visiting = new Set<string>()
  const totalFor = (cabinetId: string): number => {
    const cached = totals.get(cabinetId)
    if (cached !== undefined) return cached
    if (visiting.has(cabinetId)) return directLoad.get(cabinetId) || 0 // cycle : éviter la récursion infinie
    visiting.add(cabinetId)
    let total = directLoad.get(cabinetId) || 0
    for (const child of children.get(cabinetId) || []) total += totalFor(child)
    visiting.delete(cabinetId)
    totals.set(cabinetId, total)
    return total
  }
  for (const el of elements) {
    if (el.type === 'cabinet') totalFor(el.id)
  }
  return { direct: directLoad, total: totals }
}
